import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, FileText, Loader2, Mail, Maximize2, Minimize2, Paperclip, Phone, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";

// Panneau de clavardage CLAUDE (demande du propriétaire, 2026-10-06) : assistant
// virtuel du site — réponses en continu (SSE), session persistante (localStorage),
// bilingue FR/EN. Ouvert depuis la bulle de clavardage (« Assistant IA »).
const SALUT = {
  fr: "Bonjour ! Je suis l'assistant de Mr Trophée. Une question sur nos trophées, médailles, plaques ou la gravure personnalisée ?",
  en: "Hello! I'm the Mr Trophée assistant. Any questions about our trophies, medals, plaques or custom engraving?",
};
const TXT = {
  fr: { titre: "Assistant Mr Trophée", sous: "Réponses instantanées — IA", ph: "Écrivez votre question…", envoyer: "Envoyer", erreur: "Petit pépin technique — réessayez ou écrivez-nous à commande@trophee.ca", atelier: "Patrick — Mr Trophée",
        introTitre: "Avant de commencer…", introSous: "Créez votre compte (30 secondes) — vous recevrez nos réponses par courriel et retrouverez votre discussion à tout moment.", nomPh: "Votre nom", courrielPh: "votre@courriel.com", mdpPh: "Mot de passe (6 caractères min.)", commencer: "Créer mon compte et clavarder", dejaCompte: "J'ai déjà un compte", retourCreer: "Créer un compte", connecter: "Me connecter et clavarder", connexionSous: "Connectez-vous — votre discussion vous attend.",
        errCourriel: "Inscrivez un courriel valide", errMdp: "Mot de passe : 6 caractères minimum", errNom: "Inscrivez votre nom",
        errExiste: "Ce courriel a déjà un compte — connectez-vous ci-dessous", errLogin: "Courriel ou mot de passe incorrect", errReseau: "Petit pépin technique — réessayez",
        joindre: "Joindre un fichier", fichierTropGros: "Fichier trop lourd (80 Mo max)", fichierRefuse: "Type de fichier non accepté (images, PDF, AI, EPS, SVG, ZIP, Office…)", televerser: "Envoi du fichier…", telecharger: "Télécharger",
        rappelTitre: "On vous rappelle", rappelSous: "Laissez votre numéro — on vous appelle.", rappelPh: "Votre numéro de téléphone", rappelBtn: "Me rappeler", rappelOk: "C'est noté — on vous rappelle au plus vite !", annuler: "Annuler",
        horsTitre: "Laissez un message", horsSous: "On vous répond par courriel.", horsBtn: "Envoyer le message", horsOk: "Message envoyé — on vous répond par courriel !",
        questions: "Questions rapides", qPrix: "Combien coûte un trophée ?", qDelai: "Quel est le délai de production ?", qLivraison: "Livrez-vous ?" },
  en: { titre: "Mr Trophée Assistant", sous: "Instant answers — AI", ph: "Type your question…", envoyer: "Send", erreur: "Small technical hiccup — please retry or email us at commande@trophee.ca", atelier: "Patrick — Mr Trophée",
        introTitre: "Before we start…", introSous: "Create your account (30 seconds) — you'll get our replies by email and can come back to this conversation anytime.", nomPh: "Your name", courrielPh: "you@email.com", mdpPh: "Password (6 characters min.)", commencer: "Create my account and chat", dejaCompte: "I already have an account", retourCreer: "Create an account", connecter: "Log in and chat", connexionSous: "Log in — your conversation is waiting.",
        errCourriel: "Enter a valid email", errMdp: "Password: 6 characters minimum", errNom: "Enter your name",
        errExiste: "This email already has an account — log in below", errLogin: "Incorrect email or password", errReseau: "Small technical hiccup — please retry",
        joindre: "Attach a file", fichierTropGros: "File too large (80 MB max)", fichierRefuse: "File type not accepted (images, PDF, AI, EPS, SVG, ZIP, Office…)", televerser: "Uploading file…", telecharger: "Download",
        rappelTitre: "We'll call you back", rappelSous: "Leave your number — we'll call you.", rappelPh: "Your phone number", rappelBtn: "Call me back", rappelOk: "Noted — we'll call you back shortly!", annuler: "Cancel",
        horsTitre: "Leave a message", horsSous: "We'll reply by email.", horsBtn: "Send the message", horsOk: "Message sent — we'll reply by email!",
        questions: "Quick questions", qPrix: "How much is a trophy?", qDelai: "What's the production time?", qLivraison: "Do you deliver?" },
};

export default function ChatWidget({ open, onClose }) {
  const { lang } = useLang();
  const tt = TXT[lang] || TXT.fr;
  const [messages, setMessages] = useState([]);   // [{role: "user"|"assistant", text}]
  const [saisie, setSaisie] = useState("");
  const [enCours, setEnCours] = useState(false);
  const listeRef = useRef(null);
  const sessionRef = useRef(null);
  // COMPTE VISITEUR AVANT le clavardage (demande du propriétaire, 2026-11-01) :
  // création de compte (nom + courriel + mot de passe) ou connexion — la
  // discussion est liée au compte : réponse par courriel + retour à la
  // conversation depuis n'importe quel appareil
  const identiteRef = useRef(null);
  const [nom, setNom] = useState("");
  const [courriel, setCourriel] = useState("");
  const [mdp, setMdp] = useState("");
  const [modeConnexion, setModeConnexion] = useState(false);
  const [identOk, setIdentOk] = useState(false);
  const [identErr, setIdentErr] = useState("");
  const [identBusy, setIdentBusy] = useState(false);
  // Taille du panneau (demande du propriétaire, 2026-11-03) : miniature attachée
  // à la bulle, ou PLEINE PAGE (bouton d'agrandissement dans l'en-tête).
  const [pleinePage, setPleinePage] = useState(false);
  // Options du tchat (demande du propriétaire, 2026-11-03) : rappel téléphonique,
  // message hors ligne, questions rapides.
  const [optRappel, setOptRappel] = useState(false);
  const [optHorsLigne, setOptHorsLigne] = useState(false);
  const [rappelTel, setRappelTel] = useState("");
  const [rappelOk, setRappelOk] = useState(false);
  const [rappelBusy, setRappelBusy] = useState(false);
  const [horsMsg, setHorsMsg] = useState("");
  const [horsOk, setHorsOk] = useState(false);
  const [horsBusy, setHorsBusy] = useState(false);
  if (!sessionRef.current && typeof window !== "undefined") {
    sessionRef.current = localStorage.getItem("mt_chat_sid")
      || (() => { const s = `web-${crypto.randomUUID()}`; localStorage.setItem("mt_chat_sid", s); return s; })();
    try {
      const ident = JSON.parse(localStorage.getItem("mt_chat_identite") || "null");
      if (ident && ident.courriel) {
        identiteRef.current = ident;
        setNom(ident.nom || "");
        setCourriel(ident.courriel || "");
        setIdentOk(true);
      }
    } catch { /* pas d'identité enregistrée */ }
  }

  // À l'ouverture : RECHARGER la conversation — d'abord celle du COMPTE connecté
  // (le visiteur qui revient par le lien du courriel la retrouve, même d'un autre
  // appareil), sinon celle du session_id local
  const histChargeRef = useRef(false);
  useEffect(() => {
    if (!open || histChargeRef.current) return;
    histChargeRef.current = true;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const rc = await fetch(`${apiUrl}/api/chat/ma-conversation`, { credentials: "include" });
        const dc = rc.ok ? await rc.json() : null;
        let msgs = (dc && dc.messages) || [];
        if (dc && dc.session_id) {
          sessionRef.current = dc.session_id;
          try { localStorage.setItem("mt_chat_sid", dc.session_id); } catch { /* mode privé */ }
        }
        if (!msgs.length) {
          const r = await fetch(`${apiUrl}/api/chat/${sessionRef.current}/historique`);
          if (r.ok) msgs = (await r.json()).messages || [];
        }
        const hist = msgs.map((m) => ({
          role: m.role === "user" ? "user" : "assistant",
          text: m.texte,
          atelier: m.auteur === "atelier",
        }));
        if (hist.length) {
          setMessages(hist);
          const derniereRep = [...msgs].reverse().find((m) => m.auteur === "atelier");
          if (derniereRep) dernierAtelierAt.current = derniereRep.at || "";
          if (!identOk) setIdentOk(true);  // historique = compte déjà créé
        }
      } catch { /* pas d'historique : nouvelle conversation */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // CRÉATION DE COMPTE ou CONNEXION depuis la fenêtre de tchat (2026-11-01)
  const commencer = async () => {
    const c = courriel.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c)) { setIdentErr(tt.errCourriel); return; }
    if (!modeConnexion && !nom.trim()) { setIdentErr(tt.errNom); return; }
    if (mdp.length < 6) { setIdentErr(tt.errMdp); return; }
    setIdentErr("");
    setIdentBusy(true);
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      if (modeConnexion) {
        const res = await fetch(`${apiUrl}/api/auth/login`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ email: c, password: mdp }),
        });
        if (!res.ok) { setIdentErr(tt.errLogin); return; }
        const u = await res.json();
        identiteRef.current = { nom: u.name || "", courriel: c };
        // Lie la conversation en cours au compte, puis charge celle du compte
        await fetch(`${apiUrl}/api/chat/lier`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ session_id: sessionRef.current }),
        }).catch(() => {});
        const rc = await fetch(`${apiUrl}/api/chat/ma-conversation`, { credentials: "include" });
        if (rc.ok) {
          const dc = await rc.json();
          if (dc.session_id) {
            sessionRef.current = dc.session_id;
            try { localStorage.setItem("mt_chat_sid", dc.session_id); } catch { /* mode privé */ }
          }
          const hist = (dc.messages || []).map((m) => ({
            role: m.role === "user" ? "user" : "assistant", text: m.texte, atelier: m.auteur === "atelier",
          }));
          if (hist.length) setMessages(hist);
        }
      } else {
        const res = await fetch(`${apiUrl}/api/chat/compte`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ nom: nom.trim(), courriel: c, mot_de_passe: mdp, session_id: sessionRef.current }),
        });
        if (res.status === 409) { setModeConnexion(true); setIdentErr(tt.errExiste); return; }
        if (!res.ok) { setIdentErr(tt.errReseau); return; }
        const u = await res.json();
        identiteRef.current = { nom: u.name || nom.trim(), courriel: c };
      }
      try { localStorage.setItem("mt_chat_identite", JSON.stringify(identiteRef.current)); } catch { /* mode privé */ }
      setIdentOk(true);
    } catch {
      setIdentErr(tt.errReseau);
    } finally {
      setIdentBusy(false);
    }
  };

  // Message d'accueil à la première ouverture (seulement si aucune conversation chargée)
  useEffect(() => {
    if (open && identOk && messages.length === 0) {
      setMessages([{ role: "assistant", text: SALUT[lang] || SALUT.fr }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, identOk]);

  // Défilement automatique en bas à chaque nouveau morceau de réponse
  useEffect(() => {
    listeRef.current?.scrollTo({ top: listeRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Réponses HUMAINES de l'atelier (demande du propriétaire, 2026-11-01) : le
  // panneau interroge le serveur toutes les 5 s pendant qu'il est ouvert — une
  // réponse écrite depuis le tableau de bord apparaît ici en direct
  const dernierAtelierAt = useRef("");
  useEffect(() => {
    if (!open) return undefined;
    const sonder = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/${sessionRef.current}/reponses?apres=${encodeURIComponent(dernierAtelierAt.current)}`);
        if (!r.ok) return;
        const d = await r.json();
        for (const rep of d.reponses || []) {
          dernierAtelierAt.current = rep.at || dernierAtelierAt.current;
          setMessages((m) => [...m, { role: "assistant", text: rep.texte, atelier: true,
                                      fichier: rep.fichier || null }]);
        }
      } catch { /* réseau : on réessaiera au prochain passage */ }
    };
    sonder();
    const t = setInterval(sonder, 5000);
    return () => clearInterval(t);
  }, [open]);

  // Fichiers volumineux (demande du propriétaire, 2026-11-01) : le visiteur joint
  // logo, image, PDF… jusqu'à 80 Mo — téléversement PAR MORCEAUX (proxy) et le
  // fichier apparaît comme message dans la conversation
  const fichierRef = useRef(null);
  const [televersement, setTeleversement] = useState(0); // 0 = rien, 1-99 = %, sinon terminé
  const envoyerFichier = async (f) => {
    if (!f || televersement) return;
    if (f.size > 80 * 1024 * 1024) { toast.error(tt.fichierTropGros); return; }
    setTeleversement(1);
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const sid = sessionRef.current;
    try {
      const r0 = await fetch(`${apiUrl}/api/chat/${sid}/fichier/debut`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ filename: f.name, size: f.size, content_type: f.type }),
      });
      if (!r0.ok) {
        const d = await r0.json().catch(() => ({}));
        toast.error(d.detail || tt.fichierRefuse);
        setTeleversement(0);
        return;
      }
      const { upload_id, max_chunk } = await r0.json();
      const total = Math.ceil(f.size / max_chunk);
      for (let i = 0; i < total; i++) {
        const tranche = f.slice(i * max_chunk, (i + 1) * max_chunk);
        const rc = await fetch(`${apiUrl}/api/chat/${sid}/fichier/${upload_id}/chunk?seq=${i}`, {
          method: "PUT", headers: { "Content-Type": "application/octet-stream" }, credentials: "include",
          body: tranche,
        });
        if (!rc.ok) throw new Error("chunk");
        setTeleversement(Math.round(((i + 1) / total) * 100));
      }
      const r2 = await fetch(`${apiUrl}/api/chat/${sid}/fichier/${upload_id}/terminer`, {
        method: "POST", credentials: "include",
      });
      if (!r2.ok) throw new Error("terminer");
      const fin = await r2.json();
      setMessages((m) => [...m, { role: "user", text: "", fichier: { url: fin.url, nom: fin.nom, taille: fin.taille, image: fin.image } }]);
    } catch {
      toast.error(tt.erreur);
    } finally {
      setTeleversement(0);
      if (fichierRef.current) fichierRef.current.value = "";
    }
  };

  const envoyer = async () => {
    const texte = saisie.trim();
    if (!texte || enCours) return;
    setSaisie("");
    setEnCours(true);
    setMessages((m) => [...m, { role: "user", text: texte }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: texte,
                               nom: identiteRef.current?.nom || "",
                               courriel: identiteRef.current?.courriel || "" }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let tampon = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        tampon += decoder.decode(value, { stream: true });
        const lignes = tampon.split("\n\n");
        tampon = lignes.pop() || "";
        for (const ligne of lignes) {
          if (!ligne.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(ligne.slice(6));
            if (d.erreur) throw new Error("sse");
            if (d.delta) {
              setMessages((m) => {
                const copie = [...m];
                copie[copie.length - 1] = { role: "assistant", text: copie[copie.length - 1].text + d.delta };
                return copie;
              });
            }
          } catch (e) { if (e.message === "sse") throw e; /* sinon : morceau partiel, on attend la suite */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copie = [...m];
        copie[copie.length - 1] = { role: "assistant", text: tt.erreur, erreur: true };
        return copie;
      });
    } finally {
      setEnCours(false);
    }
  };

  // RAPPEL TÉLÉPHONIQUE (demande du propriétaire, 2026-11-03) : le visiteur
  // laisse son numéro → courriel à l'atelier pour le rappeler.
  const demanderRappel = async () => {
    const tel = rappelTel.trim();
    if (tel.replace(/\D/g, "").length < 7 || rappelBusy) return;
    setRappelBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/${sessionRef.current}/rappel`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telephone: tel, nom: identiteRef.current?.nom || nom }),
      });
      if (!res.ok) throw new Error();
      setRappelOk(true);
      setMessages((m) => [...m, { role: "assistant", text: tt.rappelOk }]);
    } catch {
      toast.error(tt.erreur);
    } finally {
      setRappelBusy(false);
    }
  };

  // MESSAGE HORS LIGNE (demande du propriétaire, 2026-11-03) : le visiteur laisse
  // un message → courriel à l'atelier avec ses coordonnées.
  const envoyerHorsLigne = async () => {
    const msg = horsMsg.trim();
    if (msg.length < 3 || horsBusy) return;
    setHorsBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/${sessionRef.current}/hors-ligne`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, nom: identiteRef.current?.nom || nom,
                               courriel: identiteRef.current?.courriel || courriel }),
      });
      if (!res.ok) throw new Error();
      setHorsOk(true);
      setHorsMsg("");
    } catch {
      toast.error(tt.erreur);
    } finally {
      setHorsBusy(false);
    }
  };

  // QUESTIONS RAPIDES (demande du propriétaire, 2026-11-03) : cliquer place la
  // question dans la saisie (le visiteur l'envoie avec Entrée / le bouton).
  const questionRapide = (q) => { if (!enCours && identOk) setSaisie(q); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="chat-claude"
          data-testid="chat-widget"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed z-[60] bg-surface border border-gold/30 shadow-2xl shadow-black/60 flex flex-col overflow-hidden ${
            pleinePage
              ? "inset-0 rounded-none"
              : "bottom-40 right-4 sm:right-6 w-[calc(100vw-2rem)] max-w-sm h-[70vh] max-h-[540px] rounded-2xl"
          }`}
        >
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gold/20 bg-ink/60">
            <span className="bg-gold text-ink rounded-full p-1.5"><Bot className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">{tt.titre}</p>
              <p className="text-[10px] uppercase tracking-widest text-gold/80">{tt.sous}</p>
            </div>
            {/* Agrandir / réduire en pleine page (demande du propriétaire, 2026-11-03) */}
            <button onClick={() => setPleinePage((v) => !v)} data-testid="chat-agrandir"
              aria-label={pleinePage ? "Réduire" : "Agrandir"} title={pleinePage ? "Réduire" : "Agrandir en pleine page"}
              className="text-ivory/50 hover:text-gold transition-colors p-1">
              {pleinePage ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} data-testid="chat-close" aria-label="Fermer"
              className="text-ivory/50 hover:text-ivory transition-colors p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={listeRef} data-testid="chat-messages" data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-3">
            {!identOk ? (
              /* COMPTE visiteur AVANT le clavardage (demande du propriétaire,
                 2026-11-01) : création de compte ou connexion — la discussion est
                 liée au compte : réponse par courriel + retour à la conversation */
              <div className="h-full flex flex-col justify-center gap-3" data-testid="chat-ident-form">
                <p className="text-sm font-semibold text-ivory">{tt.introTitre}</p>
                <p className="text-xs text-ivory/60 leading-relaxed">
                  {modeConnexion ? tt.connexionSous : tt.introSous}
                </p>
                {!modeConnexion && (
                  <input value={nom} onChange={(e) => setNom(e.target.value)}
                    placeholder={tt.nomPh} maxLength={120} data-testid="chat-ident-nom"
                    className="bg-ink border border-ivory/15 rounded-full px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors" />
                )}
                <input value={courriel} onChange={(e) => { setCourriel(e.target.value); setIdentErr(""); }}
                  type="email" placeholder={tt.courrielPh} maxLength={200} data-testid="chat-ident-courriel"
                  className={`bg-ink border rounded-full px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors ${identErr === tt.errCourriel || identErr === tt.errExiste ? "border-red-400" : "border-ivory/15"}`} />
                <input value={mdp} onChange={(e) => { setMdp(e.target.value); setIdentErr(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") commencer(); }}
                  type="password" placeholder={tt.mdpPh} maxLength={100} data-testid="chat-ident-mdp"
                  className={`bg-ink border rounded-full px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors ${identErr === tt.errMdp || identErr === tt.errLogin ? "border-red-400" : "border-ivory/15"}`} />
                {identErr && <p className="text-[11px] text-red-300" data-testid="chat-ident-erreur">{identErr}</p>}
                <button onClick={commencer} disabled={identBusy || !courriel.trim() || !mdp} data-testid="chat-ident-commencer"
                  className="bg-gold text-ink font-bold rounded-full px-5 py-3 text-sm hover:bg-champagne transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {identBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modeConnexion ? tt.connecter : tt.commencer}
                </button>
                <button onClick={() => { setModeConnexion(!modeConnexion); setIdentErr(""); }}
                  data-testid="chat-ident-bascule"
                  className="text-[11px] text-gold/80 hover:text-gold underline underline-offset-2 transition-colors">
                  {modeConnexion ? tt.retourCreer : tt.dejaCompte}
                </button>
              </div>
            ) : (
            <>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-gold text-ink rounded-br-sm"
                    : m.atelier
                      ? "bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 rounded-bl-sm"
                      : m.erreur
                        ? "bg-red-500/15 border border-red-400/40 text-red-200 rounded-bl-sm"
                        : "bg-ink border border-ivory/12 text-ivory/90 rounded-bl-sm"}`}>
                  {m.atelier && (
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1">
                      {tt.atelier} — en personne
                    </span>
                  )}
                  {m.fichier && (
                    m.fichier.image
                      ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api${m.fichier.url.startsWith("/api") ? m.fichier.url.slice(4) : m.fichier}`} target="_blank" rel="noreferrer"
                          data-testid="chat-fichier-image">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${m.fichier.url}`} alt={m.fichier.nom}
                            className="rounded-lg max-w-full max-h-40 object-contain bg-white/5" />
                          <span className="block text-[10px] text-ivory/50 mt-1">{m.fichier.nom} — {(m.fichier.taille / 1024 / 1024).toFixed(1)} Mo</span>
                        </a>
                      : <a href={`${process.env.REACT_APP_BACKEND_URL}${m.fichier.url}`} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 rounded-lg border border-ivory/15 bg-ink/60 px-3 py-2 hover:border-gold/50 transition-colors"
                          data-testid="chat-fichier-lien">
                          <FileText className="w-4 h-4 text-gold shrink-0" />
                          <span className="text-xs truncate">{m.fichier.nom}</span>
                          <span className="text-[10px] text-ivory/40 shrink-0">{(m.fichier.taille / 1024 / 1024).toFixed(1)} Mo</span>
                        </a>
                  )}
                  {m.text || (m.role === "assistant" && !m.fichier && enCours && i === messages.length - 1
                    ? <Loader2 className="w-4 h-4 animate-spin text-gold" /> : "")}
                </div>
              </div>
            ))}
            </>
            )}
          </div>
          {/* OPTIONS du tchat (demande du propriétaire, 2026-11-03) : questions
              rapides, rappel téléphonique, message hors ligne */}
          {identOk && !optRappel && !optHorsLigne && (
            <div className="px-3 pt-2 pb-1 border-t border-ivory/10 bg-ink/30" data-testid="chat-options">
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-widest text-ivory/40 mb-1.5">{tt.questions}</p>
                <div className="flex flex-wrap gap-1.5">
                  {[tt.qPrix, tt.qDelai, tt.qLivraison].map((q) => (
                    <button key={q} type="button" onClick={() => questionRapide(q)} data-testid="chat-question-rapide"
                      className="text-[11px] px-2.5 py-1 rounded-full border border-gold/30 text-gold/90 hover:bg-gold hover:text-ink transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => { setOptRappel(true); setOptHorsLigne(false); }} data-testid="chat-opt-rappel"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500 hover:text-ink transition-colors">
                  <Phone className="w-3.5 h-3.5" /> {tt.rappelTitre}
                </button>
                <button type="button" onClick={() => { setOptHorsLigne(true); setOptRappel(false); }} data-testid="chat-opt-horsligne"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors">
                  <Mail className="w-3.5 h-3.5" /> {tt.horsTitre}
                </button>
              </div>
            </div>
          )}
          {/* Panneau « On vous rappelle » */}
          {optRappel && (
            <div className="px-3 py-2.5 border-t border-emerald-400/30 bg-emerald-500/5" data-testid="chat-rappel-box">
              {rappelOk ? (
                <p className="text-xs text-emerald-300 font-medium">{tt.rappelOk}</p>
              ) : (
                <>
                  <p className="text-[11px] text-ivory/70 mb-2">{tt.rappelSous}</p>
                  <div className="flex items-center gap-2">
                    <input data-testid="chat-rappel-input" value={rappelTel} inputMode="tel"
                      onChange={(e) => setRappelTel(e.target.value)} placeholder={tt.rappelPh}
                      className="flex-1 bg-ink border border-ivory/15 rounded-full px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none" />
                    <button type="button" onClick={demanderRappel} disabled={rappelBusy || rappelTel.replace(/\D/g, "").length < 7}
                      data-testid="chat-rappel-btn"
                      className="px-3.5 py-2 rounded-full text-xs font-semibold bg-emerald-500 text-ink hover:bg-emerald-400 disabled:opacity-50 transition-colors">
                      {rappelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : tt.rappelBtn}
                    </button>
                  </div>
                </>
              )}
              <button type="button" onClick={() => { setOptRappel(false); setRappelOk(false); }} data-testid="chat-rappel-annuler"
                className="mt-1.5 text-[10px] text-ivory/40 hover:text-ivory/70 underline underline-offset-2">{tt.annuler}</button>
            </div>
          )}
          {/* Panneau « Laissez un message » (hors ligne) */}
          {optHorsLigne && (
            <div className="px-3 py-2.5 border-t border-gold/30 bg-gold/5" data-testid="chat-horsligne-box">
              {horsOk ? (
                <p className="text-xs text-gold font-medium">{tt.horsOk}</p>
              ) : (
                <>
                  <p className="text-[11px] text-ivory/70 mb-2">{tt.horsSous}</p>
                  <div className="flex items-center gap-2">
                    <input data-testid="chat-horsligne-input" value={horsMsg} maxLength={1000}
                      onChange={(e) => setHorsMsg(e.target.value)} placeholder={tt.ph}
                      className="flex-1 bg-ink border border-ivory/15 rounded-full px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                    <button type="button" onClick={envoyerHorsLigne} disabled={horsBusy || horsMsg.trim().length < 3}
                      data-testid="chat-horsligne-btn"
                      className="px-3.5 py-2 rounded-full text-xs font-semibold bg-gold text-ink hover:bg-champagne disabled:opacity-50 transition-colors">
                      {horsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </>
              )}
              <button type="button" onClick={() => { setOptHorsLigne(false); setHorsOk(false); }} data-testid="chat-horsligne-annuler"
                className="mt-1.5 text-[10px] text-ivory/40 hover:text-ivory/70 underline underline-offset-2">{tt.annuler}</button>
            </div>
          )}
          <div className="p-3 border-t border-ivory/10 bg-ink/40 flex items-center gap-2">
            <input type="file" ref={fichierRef} className="hidden" data-testid="chat-fichier-input"
              accept=".png,.jpg,.jpeg,.webp,.gif,.heic,.pdf,.ai,.eps,.svg,.psd,.zip,.doc,.docx,.txt,.csv,.xlsx"
              onChange={(e) => envoyerFichier(e.target.files?.[0])} />
            <button onClick={() => fichierRef.current?.click()} disabled={!identOk || !!televersement}
              data-testid="chat-fichier-btn" aria-label={tt.joindre} title={tt.joindre}
              className="text-ivory/60 hover:text-gold rounded-full p-2.5 border border-ivory/15 hover:border-gold/50 transition-colors disabled:opacity-50">
              {televersement ? <span className="text-[10px] font-bold text-gold">{televersement}%</span> : <Paperclip className="w-4 h-4" />}
            </button>
            <input
              value={saisie}
              disabled={!identOk}
              onChange={(e) => setSaisie(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") envoyer(); }}
              placeholder={tt.ph}
              maxLength={1000}
              data-testid="chat-input"
              className="flex-1 bg-ink border border-ivory/15 rounded-full px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors"
            />
            <button onClick={envoyer} disabled={!identOk || enCours || !saisie.trim()}
              data-testid="chat-send" aria-label={tt.envoyer}
              className="bg-gold text-ink rounded-full p-2.5 hover:bg-champagne transition-colors disabled:opacity-50">
              {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
